import { useEffect, useState } from 'react';
import { Page, Header, Content } from '@backstage/core-components';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { DETAILS_PAGE_STYLES } from './details/styles';
import { Application, DASHBOARD_CARDS, DetailViewId } from './details/types';
import { ApplicationModulesDashboard } from './details/ApplicationModulesDashboard';
import { ApplicationUrl } from './details/ApplicationUrl';
import { SecuritySummaryView } from './details/SecuritySummaryView';
import { MonitoringView } from './details/MonitoringView';
import { ComplianceView } from './details/ComplianceView';
import { InitialQuestionnaireView } from './details/InitialQuestionnaireView';
import { PlaceholderModuleView } from './details/PlaceholderModuleView';
import { CybersecurityAiAgent } from './details/CybersecurityAiAgent';
import { OverallSecurityPostureBar } from './details/OverallSecurityPostureBar';
import { OnsiteComplianceAssessmentView } from './details/OnsiteComplianceAssessmentView';

// How often to poll GET /evaluation/:id/status while an evaluation is in
// flight (REQUESTED/IN_PROGRESS), in milliseconds.
const STATUS_POLL_INTERVAL_MS = 5000;

/**
 * BusinessApplicationDetailsPage ("View 2") — redesigned per product spec:
 * an always-visible "Application Modules" dashboard grid (8 cards) with
 * dedicated detail views, replacing the old vertically-stacked
 * Questionnaire/Security Summary/Monitoring cards. Every module is
 * available for every application regardless of security impact level.
 *
 * This file owns: application data fetching, evaluation request
 * submission + status polling (the ONLY real network side effects on this
 * page), and top-level view navigation. Each module's own rendering logic
 * lives in ./details/*.tsx — see that directory for the full breakdown.
 */
export const BusinessApplicationDetailsPage = () => {
  const { ban } = useParams<{ ban: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  // Preserves the Business Applications list's filter/pagination selection
  // (carried here as a query string on the incoming route, e.g. from
  // clicking a card) so the "Back to Business Applications" link restores
  // it instead of returning to a reset list.
  const backToListSearch = location.search;
  const goToApplicationsList = () =>
    navigate(`/business-applications${backToListSearch}`);

  const [app, setApp] = useState<Application | null>(null);
  const [evaluationStatus, setEvaluationStatus] = useState<string | null>(null);
  const [evaluationRequestId, setEvaluationRequestId] = useState<string | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  const fetchApplication = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');

      // GET /applications/{ban} — dedicated single-lookup endpoint,
      // returns the application object directly (not wrapped in
      // {data: [...]}) like the list endpoint used previously.
      const res = await fetchApi.fetch(`${baseUrl}/applications/${ban}`);

      if (res.status === 404) {
        setApp(null);
        return;
      }

      const data = await res.json();
      setApp(data || null);
    } catch {
      // The page remains in its loading state when the application lookup fails.
    }
  };

  useEffect(() => {
    fetchApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ban, fetchApi, discoveryApi]);

  // Resume status polling after a page load/refresh. get-app now returns
  // the current in-flight (non-FAILED) evaluation_request's id/status for
  // this BAN's security-summary version (see get_app/handler.py's
  // _get_latest_evaluation_request()) — without this, refreshing the page
  // right after clicking "Request Evaluation" lost all visibility into
  // that request: the button had already disappeared (per
  // latest_security_summary_evaluated) but nothing indicated whether the
  // evaluation was still running or had silently failed.
  //
  // Only seeds local state when it isn't already set — a request just
  // submitted via handleRequestEvaluation() in this same session should
  // keep driving the polling loop below rather than being overwritten by
  // (matching) server data from a fetchApplication() call that fires at
  // roughly the same time.
  useEffect(() => {
    if (!app) return;
    if (evaluationRequestId) return;
    if (app.latest_evaluation_request_id) {
      setEvaluationRequestId(app.latest_evaluation_request_id);
      setEvaluationStatus(app.latest_evaluation_status ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app]);

  // Poll GET /evaluation/:id/status while an evaluation is in flight, and
  // refresh the application record (to pick up the newly generated report)
  // once it reaches a terminal state.
  useEffect(() => {
    if (!evaluationRequestId) return undefined;
    if (evaluationStatus === 'COMPLETED' || evaluationStatus === 'FAILED') {
      return undefined;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('business-applications');

        const res = await fetchApi.fetch(
          `${baseUrl}/evaluation/${evaluationRequestId}/status`,
        );

        if (cancelled) return;

        const data = await res.json();
        setEvaluationStatus(data.status ?? null);

        if (data.status === 'COMPLETED') {
          // A new report_document row now exists for this BAN —
          // refetch so the report selector picks it up.
          await fetchApplication();
        }
      } catch {
        // Polling failures are retried on the next interval.
      }
    };

    poll();
    const intervalId = window.setInterval(poll, STATUS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluationRequestId, evaluationStatus]);

  const handleRequestEvaluation = async () => {
    if (!app) return;
    setSubmitting(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('business-applications');

      // POST /evaluation/request accepts {ban, content_id?} only —
      // requested_by is derived server-side from the authenticated user,
      // never from the request body.
      const res = await fetchApi.fetch(`${baseUrl}/evaluation/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ban: app.number }),
      });

      const data = await res.json();

      if (data.status === 'EXISTS') {
        setEvaluationRequestId(data.request_id ?? null);
        setEvaluationStatus('IN_PROGRESS');
      } else if (data.request_id) {
        // 202 Accepted — start polling GET /evaluation/:id/status until
        // it reaches COMPLETED/FAILED.
        setEvaluationRequestId(data.request_id);
        setEvaluationStatus('REQUESTED');
      } else {
        setEvaluationStatus('REQUESTED');
      }
    } catch {
      // The request button remains available so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  if (!app) {
    return (
      <Page themeId="tool">
        <style>{DETAILS_PAGE_STYLES}</style>
        <Header title="Loading..." />
        <Content>
          <p className="loading-text">Loading...</p>
        </Content>
      </Page>
    );
  }

  const selectedReport = (app.reports ?? [])[0] ?? null;

  const goToDashboard = () => navigate(`/business-applications/${ban}`);
  const openModule = (view: DetailViewId) =>
    navigate(`/business-applications/${ban}/${view}`);

  return (
    <Page themeId="tool">
      <style>{DETAILS_PAGE_STYLES}</style>

      <Header title={app.name} />

      <Content>
        <div className="detail-view-header">
          <button
            type="button"
            className="back-button"
            onClick={goToApplicationsList}
          >
            <span aria-hidden="true">&#8592;</span> Back to Business
            Applications
          </button>
        </div>

        <OverallSecurityPostureBar />

        <div className="detail-container">
          {/* PRIMARY CARD */}
          <div className="primary_card">
            <h4>Application overview</h4>
            <p>
              <strong>Application Name:</strong> {app.name || 'N/A'} <br />
              <strong>BAN:</strong> {app.number} <br />
              <strong>Application URL:</strong>{' '}
              <ApplicationUrl key={app.number} value={app.application_url} />{' '}
              <br />
              <strong>Lifecycle:</strong> {app.lifecycle || 'N/A'} <br />
              <strong>Application Type:</strong> {app.application_type || 'N/A'}{' '}
              <br />
              <strong>Security Impact:</strong> {app.security_impact ?? 'N/A'}{' '}
              <br />
              <strong>Business Unit:</strong> {app.business_unit || 'N/A'}{' '}
              <br />
              <strong>Department:</strong> {app.department || 'N/A'} <br />
              <strong>Owner:</strong> {app.owner || 'N/A'}
            </p>
          </div>

          <Routes>
            <Route
              index
              element={<ApplicationModulesDashboard onOpen={openModule} />}
            />
            <Route
              path="security-summary"
              element={
                <SecuritySummaryView
                  app={app}
                  onBack={goToDashboard}
                  evaluationStatus={evaluationStatus}
                  submitting={submitting}
                  onRequestEvaluation={handleRequestEvaluation}
                />
              }
            />
            <Route
              path="initial-questionnaire"
              element={
                <InitialQuestionnaireView app={app} onBack={goToDashboard} />
              }
            />
            <Route
              path="monitoring"
              element={
                <MonitoringView
                  ban={app.number}
                  owner={app.owner}
                  onBack={goToDashboard}
                />
              }
            />
            <Route
              path="compliance/*"
              element={
                <ComplianceView
                  ban={app.number}
                  onBackToDashboard={goToDashboard}
                />
              }
            />
            <Route
              path="onsite-compliance-assessment"
              element={
                <OnsiteComplianceAssessmentView
                  ban={app.number}
                  onBack={goToDashboard}
                />
              }
            />
            {DASHBOARD_CARDS.filter(card =>
              [
                'security-tooling',
                'authentication',
                'authorization',
                'supplier-agreements',
              ].includes(card.id),
            ).map(card => (
              <Route
                key={card.id}
                path={card.id}
                element={
                  <PlaceholderModuleView
                    label={card.label}
                    onBack={goToDashboard}
                  />
                }
              />
            ))}
          </Routes>
        </div>
      </Content>

      <CybersecurityAiAgent app={app} selectedReport={selectedReport} />
    </Page>
  );
};
